import { useIntl, type IntlShape } from "react-intl";
import { ImportProblemReason } from "@/graphql/generated/types";
import type { ImportProblemFieldsFragment } from "@/graphql/generated/operations";

function problemText(intl: IntlShape, problem: ImportProblemFieldsFragment) {
  const field = problem.fieldPath ?? intl.formatMessage({
    id: "service.page.bulkYaml.problemUnknownField",
    defaultMessage: "a required field",
  });

  switch (problem.reason) {
    case ImportProblemReason.MissingValue:
      return {
        what: problem.expectedValues.length > 0
          ? intl.formatMessage(
              {
                id: "service.page.bulkYaml.problemMissingAlternative",
                defaultMessage: "At least one of these fields is required: {expected}.",
              },
              { expected: problem.expectedValues.join(", ") },
            )
          : intl.formatMessage(
              { id: "service.page.bulkYaml.problemMissing", defaultMessage: "The required field “{field}” is missing." },
              { field },
            ),
        fix: intl.formatMessage(
          problem.expectedValues.length > 0
            ? {
                id: "service.page.bulkYaml.problemMissingAlternativeFix",
                defaultMessage: "Add at least one of the listed fields with a non-empty value.",
              }
            : {
                id: "service.page.bulkYaml.problemMissingFix",
                defaultMessage: "Add the field with a value of the type shown in the format documentation.",
              },
        ),
      };
    case ImportProblemReason.UnknownProperty:
      return {
        what: intl.formatMessage(
          { id: "service.page.bulkYaml.problemUnknown", defaultMessage: "The field “{field}” is not supported here." },
          { field },
        ),
        fix: problem.expectedValues.length > 0
          ? intl.formatMessage(
              {
                id: "service.page.bulkYaml.problemUnknownFixExpected",
                defaultMessage: "Rename or remove it. Supported fields include: {expected}.",
              },
              { expected: [...problem.expectedValues.slice(0, 8), ...(problem.expectedValues.length > 8 ? ["…"] : [])].join(", ") },
            )
          : intl.formatMessage({
              id: "service.page.bulkYaml.problemUnknownFix",
              defaultMessage: "Rename or remove the field and compare the document with the format example.",
            }),
      };
    case ImportProblemReason.InvalidValue:
      return {
        what: problem.rejectedValue
          ? intl.formatMessage(
              { id: "service.page.bulkYaml.problemInvalidValue", defaultMessage: "The value “{value}” is invalid." },
              { value: problem.rejectedValue },
            )
          : intl.formatMessage({
              id: "service.page.bulkYaml.problemInvalidValueGeneric",
              defaultMessage: "A value is missing, invalid, or outside the permitted range.",
            }),
        fix: problem.suggestedValue
          ? intl.formatMessage(
              { id: "service.page.bulkYaml.problemInvalidValueSuggestion", defaultMessage: "Use “{value}” instead." },
              { value: problem.suggestedValue },
            )
          : intl.formatMessage({
              id: "service.page.bulkYaml.problemInvalidValueFix",
              defaultMessage: "Use the documented type, spelling, and permitted value range.",
            }),
      };
    case ImportProblemReason.InvalidFormat:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemInvalidFormat",
          defaultMessage: "The YAML syntax or document structure is invalid.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemInvalidFormatFix",
          defaultMessage: "Check indentation, the document envelope, value types, and required blocks against an example.",
        }),
      };
    case ImportProblemReason.InvalidReference:
      return {
        what: problem.rejectedValue
          ? intl.formatMessage(
              {
                id: "service.page.bulkYaml.problemInvalidReferenceValue",
                defaultMessage: "The referenced entry “{value}” cannot be found.",
              },
              { value: problem.rejectedValue },
            )
          : intl.formatMessage({
              id: "service.page.bulkYaml.problemInvalidReference",
              defaultMessage: "A referenced entry cannot be found.",
            }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemInvalidReferenceFix",
          defaultMessage: "Use the UUID of an existing entry, or reference a document in this file through its metadata.name.",
        }),
      };
    case ImportProblemReason.Duplicate:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemDuplicate",
          defaultMessage: "The ID or another unique value already exists.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemDuplicateFix",
          defaultMessage: "Use merge to update it. To create a new entry, omit the document's content.id or assign a new one.",
        }),
      };
    case ImportProblemReason.PermissionDenied:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemPermissionDenied",
          defaultMessage: "You do not have permission to import this company.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemPermissionDeniedFix",
          defaultMessage: "Creating a company requires company-creation access. Updating one requires an active admin membership in that company. Ask an administrator to check your access.",
        }),
      };
    case ImportProblemReason.UnsupportedModification:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemUnsupported",
          defaultMessage: "This operation or field change is not supported for this document kind.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemUnsupportedFix",
          defaultMessage: "Use one of the operations listed for this kind and leave immutable fields unchanged.",
        }),
      };
    case ImportProblemReason.DependencyFailed:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemDependency",
          defaultMessage: "This document was skipped because a referenced document failed.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemDependencyFix",
          defaultMessage: "Fix the first failed document. This dependent document can then be imported automatically.",
        }),
      };
    case ImportProblemReason.PersistenceError:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemPersistence",
          defaultMessage: "The database could not store this otherwise valid document.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemPersistenceFix",
          defaultMessage: "Retry once. If it fails again, give support the job ID shown above.",
        }),
      };
    case ImportProblemReason.UnexpectedError:
      return {
        what: intl.formatMessage({
          id: "service.page.bulkYaml.problemUnexpected",
          defaultMessage: "The import failed for an unexpected technical reason.",
        }),
        fix: intl.formatMessage({
          id: "service.page.bulkYaml.problemUnexpectedFix",
          defaultMessage: "Retry once. If it fails again, give support the job ID shown above.",
        }),
      };
  }
}

export function ImportProblemGuidance({
  problem,
  onOpenDocumentation,
}: {
  problem: ImportProblemFieldsFragment;
  onOpenDocumentation: (anchor: string) => void;
}) {
  const intl = useIntl();
  const copy = problemText(intl, problem);
  const location = problem.line
    ? intl.formatMessage(
        problem.column
          ? { id: "service.page.bulkYaml.problemLocationColumn", defaultMessage: "Near line {line}, column {column}" }
          : { id: "service.page.bulkYaml.problemLocation", defaultMessage: "Near line {line}" },
        { line: problem.line, column: problem.column },
      )
    : null;

  return (
    <div className="mt-2 rounded-md bg-red-600/[0.045] p-3 text-smycles-meta text-ink ring-1 ring-red-600/15">
      <dl className="grid gap-2 @md:grid-cols-[7rem_1fr]">
        <dt className="font-medium text-ink-muted">
          {intl.formatMessage({ id: "service.page.bulkYaml.problemWhat", defaultMessage: "What is wrong" })}
        </dt>
        <dd id="import-problem-what">{copy.what}</dd>
        <dt className="font-medium text-ink-muted">
          {intl.formatMessage({ id: "service.page.bulkYaml.problemFix", defaultMessage: "How to fix it" })}
        </dt>
        <dd id="import-problem-fix">{copy.fix}</dd>
      </dl>
      {location ? <div className="mt-2 font-mono text-smycles-micro text-ink-muted">{location}</div> : null}
      <a
        href={`#${problem.documentationAnchor}`}
        onClick={(event) => {
          event.preventDefault();
          onOpenDocumentation(problem.documentationAnchor);
        }}
        className="mt-2 inline-block text-ink-accent underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
      >
        {intl.formatMessage({
          id: "service.page.bulkYaml.problemDocumentation",
          defaultMessage: "Open format and example documentation ↓",
        })}
      </a>
    </div>
  );
}
